/**
 * lib/uploadReminders.ts
 * ------------------------------------------------------------------
 * THE TWO WEEKLY "PLEASE UPLOAD" REMINDERS — who gets them, when, and
 * where they lead.
 *
 *   clocking     — the clocking terminals' exports, into hrAttendance.
 *                  Monday 08:00. Kendra Makoni's account and system admin.
 *   ageAnalysis  — Pastel's Customer Detailed Ledger (the age analysis
 *                  until 17 Oct 2026; the kind keeps its old name), for
 *                  the comparison against the portal's fee balances.
 *                  Saturday 11:30.
 *                  The bursar (Patricia) and the operations manager
 *                  (Santosh) — bursar and operationsAdmin, both of whom
 *                  hold canImportPastel.
 *
 * Each reminder goes out three ways at once: the bell, a push to every
 * device the person enrolled, and an email. Bell and push are one write —
 * functions/pushNotify.js mirrors every reportNotifications doc to push —
 * and `details.link` is the hash route both of them open, straight onto
 * the upload screen.
 *
 * RECIPIENTS ARE ACCOUNTS, NOT PEOPLE. The list is stored as user ids
 * (`reportUploadReminders/{kind}`) and edited on the upload screen. When
 * someone leaves, their account passes to the replacement with a new
 * email and password, and the reminder follows without anyone touching
 * this list. With no list saved, the function falls back to the roles in
 * DEFAULT_ROLES.
 *
 * functions/uploadReminders.js mirrors these constants by hand (functions
 * cannot import from src/). Change one, change both.
 * ------------------------------------------------------------------ */
import type { Role } from '@/types';

export type UploadReminderKind = 'clocking' | 'ageAnalysis';

export const UPLOAD_REMINDERS_COLLECTION = 'reportUploadReminders';

export interface ReminderRecipient {
  uid: string;
  name: string;
  email: string;
}

export interface UploadReminderSettings {
  recipients: ReminderRecipient[];
  updatedAt?: string;
  updatedBy?: string;
}

export const UPLOAD_REMINDERS: Record<UploadReminderKind, {
  /** Hash route the bell and the push open. */
  link: string;
  /** When it goes out, in words, for the screen. */
  when: string;
  /** Who gets it while no list has been saved. */
  defaultRoles: Role[];
  /** Where each successful upload is logged; the reminder reads the latest. */
  uploadsCollection: string;
}> = {
  clocking: {
    link: '#/hrClockingImport',
    when: 'every Monday at 08:00',
    defaultRoles: ['superAcademicAdmin', 'systemAdmin'],
    uploadsCollection: 'hrClockingUploads',
  },
  ageAnalysis: {
    link: '#/financeAgeAnalysis',
    when: 'every Saturday at 11:30',
    defaultRoles: ['bursar', 'financeAdmin', 'operationsAdmin'],
    uploadsCollection: 'financeAgeAnalysisUploads',
  },
};

/** Who is reminded when nobody has chosen: holders of the default roles. */
export function defaultRecipients<U extends { uid: string; role?: Role; roles?: Role[] }>(
  kind: UploadReminderKind,
  users: U[],
): U[] {
  const want = UPLOAD_REMINDERS[kind].defaultRoles;
  return users.filter((u) => (u.roles?.length ? u.roles : u.role ? [u.role] : []).some((r) => want.includes(r)));
}
